import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { BrandingProvider } from './lib/branding-context';
import { ColorProvider } from './lib/ColorProvider';
import { Toaster } from 'sonner';

const App = lazy(() => import('./App'));
const LandingPage = lazy(() => import('./pages/landing'));
const FreeInvoiceTemplate = lazy(() => import('./pages/free-invoice-template'));
const AdminPortal = lazy(() => import('./pages/admin-portal'));
const PaymentSuccess = lazy(() => import('./pages/payment-success'));
const ClientPortal = lazy(() => import('./pages/client-portal'));
const MobileVerification = lazy(() => import('./pages/mobile-verification').then(m => ({ default: m.MobileVerification })));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const PayrollControlCenter = lazy(() => import('./pages/payroll/control'));
const SharedProjectView = lazy(() => import('./modules/projects/pages/SharedProjectView'));
const OwnerPortalPublicPage = lazy(() => import('./pages/OwnerPortalPublicPage'));
const SubPortalPublicPage = lazy(() => import('./pages/SubPortalPublicPage'));

function LoadingFallback() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

function AppShell() {
  return (
    <BrandingProvider>
      <ColorProvider>
        <App />
      </ColorProvider>
    </BrandingProvider>
  );
}

function MobileVerificationRoute() {
  const { token } = useParams();
  return <MobileVerification token={token || ''} />;
}

function ProjectAnalyticsRedirect() {
  const projectId =
    typeof window !== 'undefined' ? localStorage.getItem('current_project_id') : null;
  if (projectId) {
    return <Navigate to={`/projects/${projectId}/analytics`} replace />;
  }
  return <Navigate to="/projects" replace />;
}

function isPasswordRecoveryHash(hash: string) {
  if (!hash) return false;

  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return Boolean(params.get('access_token') && params.get('refresh_token') && params.get('type') === 'recovery');
}

function PasswordRecoveryRedirect() {
  const location = useLocation();

  if (location.pathname !== '/reset-password' && isPasswordRecoveryHash(location.hash)) {
    return (
      <Navigate
        to={{
          pathname: '/reset-password',
          hash: location.hash,
        }}
        replace
      />
    );
  }

  return null;
}

export default function Router() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <PasswordRecoveryRedirect />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public landing page - the homepage for visitors */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/welcome" element={<Navigate to="/" replace />} />
          <Route path="/home" element={<Navigate to="/" replace />} />

          <Route path="/admin" element={<AdminPortal />} />
          <Route path="/free-invoice-template" element={<FreeInvoiceTemplate />} />
          <Route path="/free-template" element={<Navigate to="/free-invoice-template" replace />} />
          <Route path="/download-template" element={<Navigate to="/free-invoice-template" replace />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/client-portal/*" element={<ClientPortal />} />
          <Route path="/client_hubs/*" element={<ClientPortal />} />
          <Route path="/portal/:token" element={<OwnerPortalPublicPage />} />
          <Route path="/sub-portal/:token" element={<SubPortalPublicPage />} />
          <Route path="/verify/:token" element={<MobileVerificationRoute />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Public payroll routes (no auth required, standalone pages) */}
          <Route path="/payroll/control-center" element={<PayrollControlCenter />} />
          <Route path="/payroll-control-center" element={<Navigate to="/payroll/control-center" replace />} />

          {/* Redirect helpers */}
          <Route path="/pay-cycle-setup" element={<Navigate to="/payroll/pay-cycles" replace />} />
          <Route path="/team/calendar" element={<Navigate to="/team-calendar" replace />} />
          <Route path="/leave/calendar" element={<Navigate to="/team-calendar" replace />} />
          <Route path="/leave/types" element={<Navigate to="/payroll/leave-types" replace />} />
          <Route path="/burden-alerts" element={<Navigate to="/payroll/burden-alerts" replace />} />
          <Route path="/timesheet/entry" element={<Navigate to="/timesheets/entry" replace />} />
          <Route path="/timesheet" element={<Navigate to="/timesheets/entry" replace />} />

          <Route path="/analytics" element={<ProjectAnalyticsRedirect />} />
          <Route path="/shared/project/:token" element={<SharedProjectView />} />

          {/*
            All authenticated app routes go through AppShell.
            AppShell wraps App which handles the actual route matching.
            This catch-all approach ensures the inner Routes in App.tsx
            sees the full URL path for matching.
          */}
          <Route path="/*" element={<AppShell />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
